import { STATUS } from '@front-app/constants/state';
import { sum } from '@front-app/util/math';
import { isNotNullish } from '@front-app/util/types';
import type { Ref } from '@vue/composition-api';
import { computed } from '@vue/composition-api';
import isAfter from 'date-fns/isAfter/index';
import isBefore from 'date-fns/isBefore/index';
import max from 'date-fns/max/index';
import { StepCompletion } from '@front-app/api-generated/learning-management-api';
import type { LearningProgramStore } from '../store/model';
import type { AnyLearningItem, LearningModule, LearningProgram } from '../Model';
import {
	isModule,
	isPart,
	isProgram,
	isStep,
} from './types';

export const maxDateModified = (programId: Ref<string>, store: LearningProgramStore): Ref<Date> => computed(() => {
	const pId = programId.value;
	const program = store.getProgram(pId);
	const modules = program.modules.map(store.getModule);
	const parts = modules.flatMap(({ parts: p }) => p).map(store.getPart);
	const steps = parts.flatMap(({ steps: s }) => s).map(store.getStep);
	const dates = [
		program,
		modules,
		parts,
		steps,
	].flatMap((x: AnyLearningItem | AnyLearningItem[]) => x)
		.map(({ dateModified }) => dateModified)
		.filter(isNotNullish)
		.map((dateModified) => new Date(dateModified).getTime());
	return new Date(Math.max.apply(null, dates));
});

export const isProgramAnyLoading = (programId: Ref<string>, store: LearningProgramStore): Ref<boolean> => computed(() => {
	const pId = programId.value;
	if (store.getProgramStatus(pId).value === STATUS.LOADING) {
		return true;
	}
	const { modules } = store.getProgram(pId);
	if (modules.map((id) => store.getModuleStatus(id).value).some((val) => val === STATUS.LOADING)) {
		return true;
	}
	const parts = modules.flatMap((id) => store.getModule(id).parts);
	if (parts.map((id) => store.getPartStatus(id).value).some((val) => val === STATUS.LOADING)) {
		return true;
	}
	const steps = parts.flatMap((id) => store.getPart(id).steps);
	return steps.map((id) => store.getStepStatus(id).value).some((val) => val === STATUS.LOADING);
});

export const sumCredits = (item: AnyLearningItem, store: LearningProgramStore): number => {
	if (isProgram(item) && item.modules.length === 0) {
		return item.totalCredits;
	}
	const children = getChildren(item, store);
	const credits = !isStep(item) ? item.credits : 0;
	return credits + children.map((child) => sumCredits(child, store)).reduce(sum, 0);
};

export const getProgress = (item: AnyLearningItem, store: LearningProgramStore): StepCompletion => {
	if (isProgram(item)) {
		return item.stepCompletion;
	}
	if (isStep(item)) {
		return {
			total: 1,
			completed: item.progress.dateCompleted ? 1 : 0,
			lastAction: item.progress.dateCompleted || '',
		};
	}
	return getChildren(item, store)
		.map((child) => getProgress(child, store))
		.reduce((acc, curr) => ({
			total: acc.total + curr.total,
			completed: acc.completed + curr.completed,
			lastAction: getMaxDateAsString(acc.lastAction, curr.lastAction),
		}), {
			total: 0,
			completed: 0,
			lastAction: '',
		});
};

export function getMaxDateAsString(d1: string, d2: string): string {
	if (d1 && d2) {
		return (max([new Date(d1), new Date(d2)])).toISOString();
	}
	return d1 || d2;
}

export function getMaxDateAsStringOrNull(d1?: string | null, d2?: string | null): string | null {
	if (d1 && d2) {
		return getMaxDateAsString(d1, d2);
	}
	return d1 || d2 || null;
}

export const isCompleted = (item: AnyLearningItem, store: LearningProgramStore): boolean => {
	if (isProgram(item) && hasIncompleteModules(item, store)) {
		return false;
	}
	const { total, completed } = getProgress(item, store);
	return total > 0 && completed === total;
};

function hasIncompleteModules(learningProgram: LearningProgram, store: LearningProgramStore): boolean {
	return learningProgram.modules.map(store.getModule).some((item) => !isCompleted(item, store));
}

export const getIsCompleted = (store: LearningProgramStore) => (item: AnyLearningItem): boolean => isCompleted(item, store);

export function getChildren(item: AnyLearningItem, store: LearningProgramStore): AnyLearningItem[] {
	if (isProgram(item)) {
		return item.modules.map(store.getModule);
	}
	if (isModule(item)) {
		return item.parts.map(store.getPart);
	}
	if (isPart(item)) {
		return item.steps.map(store.getStep);
	}
	return [];
}

export function isProgramPublished(item: LearningProgram): boolean {
	const { publishDateEnd, publishDateStart } = item;
	return publishDateStart !== null && isBefore(new Date(publishDateStart), new Date())
		&& (!publishDateEnd || isAfter(new Date(publishDateEnd), new Date()));
}

export function isModulePublished(item: LearningModule): boolean {
	const { publishDateStart } = item;
	return publishDateStart !== null && isBefore(new Date(publishDateStart), new Date());
}
